import useBreakpoint from 'hooks/useBreakpoint';
import { usePathname } from 'next/navigation';
import { ensureUrlTrailingSlash } from 'utils/stringUtils';
import { COMMON_TEST_IDS } from 'constants/testsIds/common';
import type { PagePagination } from 'utils/miscUtils/calculatePageInfo';
import * as S from './PaginationStyle';

export interface PaginationProps {
  pagination: PagePagination;
}

interface PaginationItemProps {
  pageNumber: number;
  isCurrentPage: boolean;
  pathname: string;
}

interface PreviousPageArrowProps {
  currentPage: number;
  pathname: string;
}

interface NextPageArrowProps {
  currentPage: number;
  pagesCount: number;
  pathname: string;
}

const PAGES_SLOTS = 9;
let hiddenPages = [] as number[];

const PreviousPageArrow = ({ currentPage, pathname }: PreviousPageArrowProps) => {
  const previousPage = currentPage - 1;
  const isCurrentPageFirst = currentPage === 1;
  const isPrevPageFirst = currentPage - 1 === 1;
  const href = isPrevPageFirst ? pathname : `${pathname}page/${previousPage}/`;

  return (
    <li data-cy={COMMON_TEST_IDS.PAGINATION_PREVIOUS}>
      <S.ArrowLink
        {...(isCurrentPageFirst ? { role: 'link' } : { href })}
        aria-disabled={isCurrentPageFirst}
        aria-label='Go to previous page'
      >
        <S.PreviousArrow $isDisabled={isCurrentPageFirst} />
      </S.ArrowLink>
    </li>
  );
};

const NextPageArrow = ({ currentPage, pagesCount, pathname }: NextPageArrowProps) => {
  const nextPage = currentPage + 1;
  const isCurrentPageLast = currentPage === pagesCount;
  const href = `${pathname}page/${nextPage}/`;

  return (
    <li data-cy={COMMON_TEST_IDS.PAGINATION_NEXT}>
      <S.ArrowLink
        {...(isCurrentPageLast ? { role: 'link' } : { href })}
        aria-disabled={isCurrentPageLast}
        aria-label='Go to next page'
      >
        <S.NextArrow $isDisabled={isCurrentPageLast} />
      </S.ArrowLink>
    </li>
  );
};

const NonVisiblePagesEllipsis = () => (
  <li>
    <span
      aria-label='Ellipsis for non-visible pages'
      role='presentation'
    >
      ...
    </span>
  </li>
);

const PaginationItem = ({ pageNumber, isCurrentPage, pathname }: PaginationItemProps) => {
  const ariaLabel = isCurrentPage ? `Current Page, Page ${pageNumber}` : `Page ${pageNumber}`;
  const isPageFirst = pageNumber === 1;
  const href = `${pathname}page/${pageNumber}/`;

  return (
    <li data-cy={`${COMMON_TEST_IDS.PAGINATION_NUMBER}-${pageNumber}`}>
      <S.PageLink
        href={isPageFirst ? pathname : href}
        aria-label={ariaLabel}
        {...(isCurrentPage && { 'aria-current': 'page' })}
        type={isCurrentPage ? 'brandAccentPrimary' : 'brandSecondary'}
      >
        {pageNumber}
      </S.PageLink>
    </li>
  );
};

const Pagination = ({ pagination }: PaginationProps) => {
  // @ts-ignore
  const cleanPathName = usePathname().replace('/us', ''); // this fix is need for well that rewrites '/well/us' to '/well'
  const pathname = ensureUrlTrailingSlash(cleanPathName.split('/page/')[0]);
  const isMobile = useBreakpoint('mdMax');

  if (!pagination) return null;

  const { pagesCount, currentPage } = pagination;

  const mapToPaginationItems = (pagesList: Array<number | '...' | null> | null) =>
    pagesList?.map((page) => {
      if (page === null) return null;
      if (typeof page === 'number') {
        return (
          <PaginationItem
            key={page}
            pageNumber={page}
            isCurrentPage={page === currentPage}
            pathname={pathname}
          />
        );
      }
      return <NonVisiblePagesEllipsis key={page} />;
    });

  // Returns the nth previous page if it exists and null if not.
  const getPreviousPage = (n = 1) => {
    const previousPage = currentPage - n;
    const previousPageExists = previousPage >= 1;

    return previousPageExists ? previousPage : null;
  };

  // Returns the nth next page if it exists and null if not.
  const getNextPage = (n = 1) => {
    const nextPage = currentPage + n;
    const nextPageExists = nextPage <= pagesCount;

    return nextPageExists ? nextPage : null;
  };

  /* The pagination will have a maximum of 9 slots on desktop and maximum 3 slots on mobile.
  Each slot contains a page number or an ellipsis to mark non-visible pages.
  The function returns an array with the corresponding slots.
   */
  const getPaginationSlots = (showMobilePagination: boolean | undefined) => {
    if (showMobilePagination === undefined) return null;

    // Mobile: The current, next and previous pages are displayed if they exist (maximum 3 page slots)
    if (showMobilePagination) {
      const previousPage = getPreviousPage();
      const nextPage = getNextPage();
      let mobilePaginationNumbers = [previousPage, currentPage, nextPage];

      // When the current page is the first one show the next two pages if they exist.
      const isFirstPage = currentPage === 1;
      if (isFirstPage) {
        const secondNextPage = getNextPage(2);
        mobilePaginationNumbers = [...mobilePaginationNumbers, secondNextPage];
      }

      // When the current page is the last one show the previous two pages if they exist.
      const isLastPage = currentPage === pagesCount;
      if (isLastPage) {
        const secondPreviousPage = getPreviousPage(2);
        mobilePaginationNumbers = [secondPreviousPage, ...mobilePaginationNumbers];
      }

      return mobilePaginationNumbers;
    }

    // Desktop: The first, last and current pages are always displayed.
    // If they exist the previous two pages and next two pages are also shown.
    // All the other pages are considered non-visible and marked with an ellipsis.
    const allPagesNumbers = Array.from({ length: pagesCount }, (_, el) => el + 1);

    // Show all the pages if there are fewer than 9 pages
    if (pagesCount < PAGES_SLOTS) {
      return allPagesNumbers;
    }

    let isPageNumberNotVisible: boolean;
    hiddenPages = [];
    return allPagesNumbers.map((pageNumber) => {
      const isFirstPage = pageNumber === 1;
      const isLastPage = pageNumber === pagesCount;
      const isWithinTwoPagesFromCurrentPage = Math.abs(pageNumber - currentPage) <= 2;

      // Only show the first, previous two, current, next two and last pages
      if (isFirstPage || isLastPage || isWithinTwoPagesFromCurrentPage) {
        isPageNumberNotVisible = false;
        return pageNumber;
      }

      // Show an ellipsis for non-visible pages
      if (!isPageNumberNotVisible) {
        isPageNumberNotVisible = true;
        hiddenPages.push(pageNumber);
        return '...';
      }

      hiddenPages.push(pageNumber);
      return null;
    });
  };

  return (
    <nav aria-label='Pagination'>
      <S.PaginationList data-cy={COMMON_TEST_IDS.PAGINATION_WRAPPER}>
        <PreviousPageArrow
          currentPage={currentPage}
          pathname={pathname}
        />
        {mapToPaginationItems(getPaginationSlots(isMobile))}

        {hiddenPages.map((pageNumber) => {
          const href = `${pathname}page/${pageNumber}/`;
          const ariaLabel = `Page ${pageNumber}`;
          return (
            <S.HiddenLi
              key={pageNumber}
              data-cy={`${COMMON_TEST_IDS.PAGINATION_NUMBER_HIDDEN}-${pageNumber}`}
            >
              <S.PageLink
                href={href}
                aria-label={ariaLabel}
              >
                {pageNumber}
              </S.PageLink>
            </S.HiddenLi>
          );
        })}

        <NextPageArrow
          currentPage={currentPage}
          pagesCount={pagesCount}
          pathname={pathname}
        />
      </S.PaginationList>
    </nav>
  );
};

export default Pagination;
